import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {map} from "rxjs/operators";
import {IDropdownItem} from "@irb-common/models/dropdown.model";


export interface IAutoRebalancePortfolioData {
  autoRebalPfExist: boolean;
  nextAutoRebalDate: string;
  autoRebalPfOrdersCount: number;
  autoRebalPfNoOrdersCount: number;
  autoRebalErrorCount: number;
  totalAutoRebalPfCount: number;
  totalScheduledAutoRebalPfCount: number;
}

export interface IAutoRebalanceOrderData {
  requiresAttentionCount: number;
  filledCount: number;
  pendingCount: number; //new
  totalCount: number; //new
  approvedCount?: number;
  errorCount?: number;
  submitedCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class IrbDashboardService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
    })
  };

  selectedMasterAccounts: Array<string> = [];
  masterAccountsDataSource = new BehaviorSubject<any>(null);
  currentMasterAccountData: Observable<any> = this.masterAccountsDataSource.asObservable();

  constructor(private _http: HttpClient) {
  }

  getGlobalSettings(): Observable<Object> {
    return this._http.get("/irebalone/home-page/get-global-settings", this.httpOptions);
  }

  getPfValueDetails(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-pf-value-details", this.httpOptions);
  }
  getUnassignAccValueDetails(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-unassign-acc-value-details", this.httpOptions);
  }
  getPfWithHPNotesCount(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-pf-with-hp-notes-count", this.httpOptions);
  }
  getPfPendingResultCount(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-pf-pending-result-count", this.httpOptions);
  }
  gettaxHarExpCount(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-tax-har-exp-count", this.httpOptions);
  }

  getAllocationReportDetails(): Observable<Object>{
    return this._http.get("/irebal-rprt/alloc-report-service/get-allocation-report-details", this.httpOptions)
    .pipe(map(res => this.defineResponse(res)));
  }

  getRebalanceDetails(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-rebalance-details", this.httpOptions);
  }

  getCashAnalysisDetails(): Observable<Object>{
    return this._http.get("/irb-cash-rprt/reports/get-cash-analysis-details", this.httpOptions);
  }

  getTopModels(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-top-models", this.httpOptions);
  }

  getAdvisorInvoiceList(): Observable<Object>{
    let url = `/model-market-service/get-advisor-invoice-list`;
    return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  getSystemMessages(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-system-messages", this.httpOptions);
  }

  getSystemMessageInventory(): Observable<Object>{
    return this._http.get("/irebalone/settings/announcements", this.httpOptions);
  }

  getBlotterDetailsActive(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-blotter-details-active", this.httpOptions);
  }
  getBlotterDetailsFilled(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-blotter-details-filled", this.httpOptions);
  }
  getMMCDetails(): Observable<Object>{
    return this._http.get("/irebalone/home-page/get-mmc-details", this.httpOptions);
  }

  downloadInvoices(id: number): any {
    const options: any = {responseType: 'arraybuffer'};
    let url = `/model-market-service/get-advisor-invoice/${id}`;
    return this._http.get(url, options).pipe(map(res => this.defineResponse(res)));
  }

  getAutoRebalancePortfolioData(): Observable<Object>{
    return this._http.get("/irebalone/home-page/auto-rebalance/portfolios-count", this.httpOptions);
  }

  getAutoRebalanceOrderData(): Observable<Object> {
    return this._http.get("/irebalone/home-page/get-automated-blotter-details", this.httpOptions);
  }
  defineResponse(res: any) {
      return  res && res.data && res.data.irebalResponse ? res.data.irebalResponse : res;
  }

  updateMasterAccountsData(masterAccounts: any): void {
    this.selectedMasterAccounts = masterAccounts && masterAccounts.length > 0 ? masterAccounts.map((account: IDropdownItem) => account.value) : null;
    this.masterAccountsDataSource.next(this.selectedMasterAccounts);
  }

  // clients module calls by master accounts
  getUnassignAccValueDetailsForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/unassigned-accounts-count", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  getPfPendingResultCountForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/portfolio-pending-rebalance-result-count", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  gettaxHarExpCountForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/taxloss-harvest-expiration-count", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  //order blotter calls by master account
  getBlotterActiveOrdersForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/blotter-active-orders-count", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  getBlotterFilledOrdersForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/blotter-filled-orders-count", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  // cash report call by master accounts
  getCashAnalysisForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irb-cash-rprt/reports/cash-analysis-details-by-masters", masterAccounts, this.httpOptions)
      .pipe(map(res => this.defineResponse(res)));
  }

  // allocation report module by master accounts
  getAllocationReportDetailsForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebal-rprt/alloc-report-service/allocation-report-details", masterAccounts, this.httpOptions)
      .pipe(map(res => this.defineResponse(res)));
  }

  // rebalances module by master accounts
  getRebalanceDetailsForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/rebalances-info", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  // top models module  by master accounts
  getTopModelsForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/ma-top-models", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  // auto rebalance module portfolios by master accounts
  getAutoRebalancePortForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/auto-rebalance/ma-portfolios-count", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  // auto rebalancing module
  getAutoRebalanceBlotterForMasters(masterAccounts: any): Observable<Object>{
    return this._http.post("/irebalone/home-page/automated-blotter-details", masterAccounts, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

}
